import type { ParamDeclaration } from "@uirouter/react";

import {
    openCountryFilterDSSelected,
    countryFilterDSSelected,
    durationFilterDSSelected,
    isStillNewSession,
    webSourceFilterDSSelected,
} from "app/actions/usersFiltersSelectionActions";
import { swSettings } from "app/common/services/swSettings";
import { SwRedux } from "app/services/SwRedux";

const DASHBOARD_STATE_ID = "dashboardId";

export enum EUrlKeys {
    KEY = "key",
    KEYWORD = "keyword",
    KEYWORDS = "keywords",
    INDUSTRY = "category",
    SEGMENT = "id",
}

export const filterConsistencyParams = {
    isDurationChanged: null,
    isCountryChanged: null,
    isWebSourceChanged: null,
    ignoreFilterConsistency: false,
};

export type FilterConsistencyParamKeys =
    | "ignoreFilterConsistency"
    | "isDurationChanged"
    | "isCountryChanged"
    | "isWebSourceChanged";

export const filterConsistencyParamDeclarations: Record<FilterConsistencyParamKeys, ParamDeclaration> = {
    ignoreFilterConsistency: {
        type: "bool",
        array: false,
        value: filterConsistencyParams.ignoreFilterConsistency,
    },
    isDurationChanged: {
        type: "bool",
        array: false,
        value: filterConsistencyParams.isDurationChanged,
    },
    isCountryChanged: {
        type: "bool",
        array: false,
        value: filterConsistencyParams.isCountryChanged,
    },
    isWebSourceChanged: {
        type: "bool",
        array: false,
        value: filterConsistencyParams.isWebSourceChanged,
    },
};

export interface IFilterHandlerReturnObj {
    isAllowed?: boolean;
    displayChangeIndicator: boolean;
    isChanged: boolean;
    shouldChangeToSelected?: boolean;
    value?: string;
}

export interface IParams {
    [p: string]: string | number;
}

const isExpandingSideBar = (toParams: FixmeType, key: FixmeType) => {
    // is not generic indication and each section/module could be another parameter that will be undefined
    return toParams[key] === undefined;
};

export const shouldApplyFilterConsistency = (fromState: FixmeType, toParams: FixmeType, urlKey: FixmeType) => {
    // didUserComeFromLink & didUserComeFromDashboard are standing for product decision when user come
    // from link for example with WW & 6M and his selected filters are usa & 12m,
    // we don't want that the filter consistency will override the link filter, till the next navigation.
    const didUserComeFromLink = fromState.name === "" && fromState.url === "^";
    const didUserComeFromDashboard = fromState.stateId === DASHBOARD_STATE_ID;
    const isExpandingSideBarMode = isExpandingSideBar(toParams, urlKey);
    const ignoreFilterConsistency = toParams.ignoreFilterConsistency;
    return !ignoreFilterConsistency && !isExpandingSideBarMode && !didUserComeFromLink && !didUserComeFromDashboard;
};

export const saveFilterConsistencyAfterFirstNavigation = (targetState: FixmeType, toParams: FixmeType) => {
    const swRedux = SwRedux.getInstance();
    // we want to save the filters after the first navigation as selected filters
    swRedux.dispatch(durationFilterDSSelected(toParams.duration));
    swRedux.dispatch(webSourceFilterDSSelected(toParams.webSource || targetState.defaultQueryParams?.webSource));
    swRedux.dispatch(countryFilterDSSelected(toParams.country));
    swRedux.dispatch(isStillNewSession(false));
};

export const testAndApplyChanges = (
    countryChanges: IFilterHandlerReturnObj,
    durationChanges: IFilterHandlerReturnObj,
    webSourceChanges: IFilterHandlerReturnObj,
    country: number,
    duration: string | undefined,
    webSource: string,
    isDurationChanged: boolean | null,
    isWebSourceChanged: boolean | null,
) => {
    return {
        ...(countryChanges.displayChangeIndicator && {
            isCountryChanged: countryChanges.isChanged,
        }),
        ...((durationChanges.displayChangeIndicator ||
            Boolean(isDurationChanged) !== Boolean(durationChanges.isChanged)) && {
            isDurationChanged: durationChanges.isChanged,
        }),
        ...((webSourceChanges.displayChangeIndicator ||
            Boolean(isWebSourceChanged) !== Boolean(webSourceChanges.isChanged)) && {
            isWebSourceChanged: webSourceChanges.isChanged,
        }),
        ...(countryChanges.shouldChangeToSelected && {
            country,
        }),
        ...(durationChanges.shouldChangeToSelected && {
            duration,
        }),
        ...(webSourceChanges.shouldChangeToSelected && {
            webSource,
        }),
    };
};

export const cleanDigitalSuiteFiltersLocalStorage = () => {
    const usersWAFiltersSelectionStr = window.localStorage.getItem("usersWAFiltersSelection");
    if (usersWAFiltersSelectionStr) {
        const { usersWAFiltersSelection } = JSON.parse(usersWAFiltersSelectionStr);
        if (usersWAFiltersSelection) {
            const usersDigitalSuiteFiltersSelection = {
                ...(usersWAFiltersSelection.websiteAnalysisSelectedCountry && {
                    digitalSuiteSelectedCountry: usersWAFiltersSelection.websiteAnalysisSelectedCountry,
                }),
                ...(usersWAFiltersSelection.websiteAnalysisSelectedDuration && {
                    digitalSuiteSelectedDuration: usersWAFiltersSelection.websiteAnalysisSelectedDuration,
                }),
                ...(usersWAFiltersSelection.websiteAnalysisSelectedWebSource && {
                    digitalSuiteSelectedWebSource: usersWAFiltersSelection.websiteAnalysisSelectedWebSource,
                }),
                ...(typeof usersWAFiltersSelection.isNewSession !== "undefined" && {
                    isNewSession: usersWAFiltersSelection.isNewSession,
                }),
            };
            window.localStorage.setItem(
                "usersDigitalSuiteFiltersSelection",
                JSON.stringify({ usersDigitalSuiteFiltersSelection }),
            );
        }
        window.localStorage.removeItem("usersWAFiltersSelection");
    }
};

export const updateFilterConsistencyCountry = (countryCode: number) => {
    const swRedux = SwRedux.getInstance();
    // If user selected a country that is not part of his countries package
    // (we have pages that have access to all countries regardless the countries the user bought
    // i.e paid landing pages, search ads 2.0, videos, creatives)
    // we save the selcted country ino open country filter instead of country filter,
    // so that we can keep it selected, when navigating within pages that have access to all countries
    const isCountryPartOfUsersPackage = swSettings.allowedCountry(countryCode, "Home");
    if (isCountryPartOfUsersPackage) {
        swRedux.dispatch(openCountryFilterDSSelected(undefined));
        swRedux.dispatch(countryFilterDSSelected(countryCode));
    } else {
        swRedux.dispatch(openCountryFilterDSSelected(countryCode));
    }
};
